import {firstValueFrom} from 'rxjs';
import {IBaseCRUDComponent} from '../../../../../sdk/src/components/ibase-crud/ibase-crud.component';
import {
    NewEditOrganizationModalComponent as NewEditOrganizationModalComponent,
    NewEditOrganizationModalConfig as NewEditOrganizationModalConfig,
    NewEditOrganizationModalResult as NewEditOrganizationModalResult
} from '../../../modals/new-edit-organization-modal/new-edit-organization-modal.component';
import {Component} from '@angular/core';
import {ETableColDataType, ETableColFilterType} from '../../../../../sdk/src/components/generic-table/table-col-def';
import {PrimeNgImportsModule} from '../../../../../sdk/src/prime-ng-imports';
import {EDeleteMode} from '../../../../../sdk/src/enums/delete-mode.enum';
import {CpmCOrganizationApiService} from '../../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-organization.api-service';
import {CpmCOrganizationApiModel} from '../../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-organization.api-model';
import {CustomerUrlBuilderUtil} from '../../../utils/customer-url-builder.util';
import {SdkImportsModule} from '../../../../../sdk/src/sdk-imports';

/**
 * Organization CRUD table component
 */
@Component({
    selector: 'app-organization-crud',
    templateUrl: './organization-crud.component.html',
    imports: [PrimeNgImportsModule, SdkImportsModule]
})
export class OrganizationCRUDComponent extends IBaseCRUDComponent<
    CpmCOrganizationApiModel,
    NewEditOrganizationModalComponent,
    NewEditOrganizationModalConfig,
    NewEditOrganizationModalResult
> {
    protected helpVisible = false;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure({
            // Model config
            modelName: 'OrganizationApiModel',
            getModelHumanIdentifier: (model: CpmCOrganizationApiModel) => model.name ?? '<Unknown organization>',

            // Service config
            deleteMode: EDeleteMode.CONFIRM_REENFORCED,
            deleteModelService: (modelToDelete: CpmCOrganizationApiModel) =>
                CpmCOrganizationApiService.deleteOrganization(modelToDelete.uuid!),

            // Modal config
            modalType: NewEditOrganizationModalComponent,
            getDialogServiceParameters: NewEditOrganizationModalComponent.getDialogServiceParameters,
            modalExtendedConfigBuilder: (model?: CpmCOrganizationApiModel) => new NewEditOrganizationModalConfig(model),

            // Authorization config
            authorizationNew: CpmCOrganizationApiService.endpointNew(this.FAKE_UUID),
            authorizationEdit: CpmCOrganizationApiService.endpointEdit(this.FAKE_UUID, this.FAKE_UUID),
            authorizationDelete: CpmCOrganizationApiService.endpointDelete(this.FAKE_UUID, this.FAKE_UUID)
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onReady(): Promise<void> {}
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On refresh (Error handling is done in the parent)
     */
    public override async onRefresh(): Promise<void> {
        this.loadingStr = 'Loading organizations...';

        const result = await firstValueFrom(CpmCOrganizationApiService.getOrganizationList({}, this.paginatorConfig));

        this.items = result.data;
        this.totalItemCount = result.total;
    }

    // ===============================================================
    // ===============================================================

    /**
     * On help button pressed
     */
    public override async onHelp(): Promise<void> {
        this.helpVisible = true;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Init the columns (Error handling is done in the parent)
     */
    protected override async initColumns(): Promise<void> {
        this.tableCols = [
            {
                field: 'name',
                header: 'Name',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT
            },
            {
                field: 'description',
                header: 'Description',
                dataType: ETableColDataType.LONG_TEXT,
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT
            }
        ];

        this.addCreatedColumns();
        this.addCommunicationButtons();

        this.addAccessButtonColumn((row) => {
            const customerUUID = this.globalsService.sessionService.getCustomer()?.uuid;
            this.globalsService.router.navigate([CustomerUrlBuilderUtil.customerOrganization(customerUUID, row.uuid)]);
        });
    }
}
